import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
    Autocomplete,
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField, Tooltip,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {isSpecialBin, MAP_KEY_SEP_CHAR, parseBinName, SaveInventoryItem, SaveInventoryRequestItem} from '../../utils'
import {webEvents} from '../../../common/WebEvents'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import {ExcludedDetailsDialog, ItemStatus, StatusTooltip} from '../../../common/ui'

interface SerialsEditProps {
    serialItems: SaveInventoryItem[]
    selectedSection: string | null
    onSelectSection: (section: string) => void
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    currentBin: string | null
}

function SerialsEdit({
                         serialItems,
                         selectedSection,
                         onSelectSection,
                         saveRequestItem,
                         currentBin
                     }: SerialsEditProps) {
    const [newSerial, setNewSerial] = useState('')
    const [filterText, setFilterText] = useState('')
    const [filteredBinSerialItems, setFilteredBinSerialItems] = useState<
        SaveInventoryItem[] | []
    >([])
    const [open, setOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState<SaveInventoryItem | null>(null)
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [savedExcludedQty, setSavedExcludedQty] = useState<number | null>(0)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    let inputRef = useRef<HTMLInputElement>(null)
    const audioPlayer = useRef<HTMLAudioElement | null>(null)

    useEffect(() => {
        if (window.sessionStorage.getItem('blocking') === 'true' && inputRef.current) {
            inputRef.current.blur()
        } else if (inputRef.current) {
            inputRef.current.focus()
        }
    }, [inputRef, window.sessionStorage.getItem('blocking')])

    useEffect(() => {
        if (selectedSection === 'serials' && inputRef.current) {
            inputRef.current.focus()
        }
    }, [selectedSection])

    const playAudio = () => {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    const handleClickOpen = (item: SaveInventoryItem) => {
        setSelectedItem(item)
        setOpen(true)
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    const handleClose = () => {
        setOpen(false)
        setSelectedItem(null)
    }

    const handleCopy = (str: string) => {
        navigator.clipboard.writeText(str)
            .then(() => {
                alert('Copied to clipboard!')
            })
            .catch(err => {
                console.error('Failed to copy: ', err)
            })
    }

    const binSerialItems = useMemo(() => {
        if (!currentBin)
            return []

        let result = serialItems.filter(x => x.bin === currentBin).reverse()
        let serialQty = 0
        let serialExcludedQty = 0
        let serialPendingQty = 0

        for (let item of result) {
            if (item.progress) {
                serialPendingQty += item.quantity
                if (item.progress === 'q-delete' || item.progress === 'deleting' && item.excluded) {
                    serialExcludedQty += item.quantity
                } else if (item.progress === 'q-delete' || item.progress === 'deleting') {
                    serialQty += item.quantity
                }
            } else if (item.excluded) {
                serialExcludedQty += item.quantity
            } else {
                serialQty += item.quantity
            }
        }

        setFilteredBinSerialItems(result)
        setSavedQty(serialQty)
        setSavedExcludedQty(serialExcludedQty)
        setPendingQty(serialPendingQty)
        return result
    }, [serialItems, currentBin])

    const handleAddSerial = async () => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }

        let currentBinName = currentBin
            ? currentBin.startsWith('01') || isSpecialBin(currentBin) ? currentBin : `01 ${currentBin}`
            : null

        if (newSerial) {
            saveRequestItem({
                op: 'add',
                sku: '',
                quantity: 1,
                serial: newSerial,
                bin: currentBinName,
                enteredAt: new Date().toISOString()
            })

            setNewSerial('')
            setFilterText('')
            setFilteredBinSerialItems(binSerialItems)
        } else {
            webEvents.warning('serial not found', {serial: newSerial})
        }
    }

    const handleRemoveSerial = (serial: string) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }

        let itemToRemove = binSerialItems.find(item => item.serial === serial)

        if (itemToRemove)
            saveRequestItem({
                op: 'delete',
                sku: '',
                quantity: 1,
                serial: itemToRemove.serial,
                bin: itemToRemove.bin,
                enteredAt: itemToRemove.enteredAt
            })
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            if (window.sessionStorage.getItem('blocking') === 'true') {
                playAudio()
                return
            }
            event.preventDefault()
            handleAddSerial()
        }
    }

    const handleInputChange = (newValue: string | '') => {
        setFilterText(newValue)

        if (newValue) {
            let filterRegex = new RegExp(newValue, 'i')

            let filteredItems = binSerialItems.filter(
                item =>
                    filterRegex.test(item.sku) ||
                    filterRegex.test(item?.serial ?? '')
            )

            setFilteredBinSerialItems(filteredItems)
        } else {
            setFilteredBinSerialItems(binSerialItems)
        }
    }

    return (
        <Box
            flex={1}
            p={2}
            border={2}
            borderColor={
                selectedSection === 'serials' ? 'primary.main' : 'grey.400'
            }
            borderRadius={2}
            onClick={e => {
                e.stopPropagation()
                onSelectSection('serials')
                window.sessionStorage.setItem('selectedSection', 'serials')
            }}
            position="relative">
            <audio ref={audioPlayer} src={excludedSound} />
            {selectedSection !== 'serials' && (
                <IconButton
                    size="small"
                    onClick={() => {
                        onSelectSection('serials')
                        window.sessionStorage.setItem('selectedSection', 'serials')
                    }}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8
                    }}>
                    <EditIcon />
                </IconButton>
            )}
            <div className="flex flex-row justify-between">
                <Typography variant="h5">Scan/Enter Serials</Typography>
                {selectedSection === 'serials' && binSerialItems.length > 0 && (
                    <Autocomplete
                        freeSolo
                        openOnFocus
                        options={Array.from(new Set(binSerialItems.map(item => item.sku)))}
                        value={filterText}
                        onInputChange={(_e, newValue) =>
                            handleInputChange(newValue)
                        }
                        onChange={(_e, newValue) => {
                            if (newValue) {
                                let filteredItem = binSerialItems.find(
                                    item =>
                                        item.sku ===
                                        newValue.split(
                                            MAP_KEY_SEP_CHAR
                                        )[0] &&
                                        item.serial ===
                                        newValue.split(MAP_KEY_SEP_CHAR)[1]
                                )

                                if (filteredItem) {
                                    setFilteredBinSerialItems([filteredItem])
                                }
                            } else {
                                setFilteredBinSerialItems(binSerialItems)
                            }
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Filter saved items"
                                variant="outlined"
                                size="small"
                                sx={{width: 200}}
                            />
                        )}
                    />
                )}
            </div>
            <Typography variant="subtitle1" color="textSecondary">
                <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                {savedQty === 1 ? `${savedQty} serial` : `${savedQty} serials`},{' '}
                <span style={{color: 'red'}}>{savedExcludedQty} excluded </span> <span
                style={{fontWeight: 700}}>Pending: </span>
                {pendingQty} quantity
            </Typography>

            {selectedSection === 'serials' && (
                <TextField
                    label="Scan Serial"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={newSerial}
                    onChange={e => setNewSerial(e.target.value.trim())}
                    onKeyDown={handleKeyDown}
                    disabled={window.sessionStorage.getItem('blocking') === 'true'}
                    placeholder="Scan or enter a serial number"
                    inputRef={inputRef}
                    data-testid="serial"
                />
            )}

            <TableContainer
                component={selectedSection === 'serials' ? Paper : 'div'}
                className="my-3"
                style={{
                    maxHeight: 400,
                    overflow: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: 4
                }}>
                <Table size="small">
                    <TableHead>
                        <TableRow
                            style={{
                                borderBottom:
                                    filteredBinSerialItems.length > 0 &&
                                    filteredBinSerialItems[0].excluded
                                        ? 'none'
                                        : '2px solid #ddd'
                            }}>
                            <TableCell sx={{display: 'none'}}></TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell>Serial</TableCell>
                            <TableCell>Status</TableCell>
                            {selectedSection === 'serials' && (
                                <TableCell>Remove</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {binSerialItems.length > 0 ? (
                            filteredBinSerialItems.map(item => {
                                // done here so summary data still shows excluded item. However, we don't want
                                // to render the row as excluded while queued for deletion or being deleted.
                                const excluded = item.progress === 'q-delete' || item.progress === 'deleting'
                                    ? '' : item.excluded
                                return (
                                    <TableRow
                                        key={item.serial}
                                        sx={{
                                            border: item.excluded ? '2px solid red' : 'none',
                                            color: item.excluded ? 'red' : 'inherit'
                                        }}>
                                        <TableCell
                                            sx={{display: 'none'}}></TableCell>
                                        <TableCell>{item.sku}</TableCell>
                                        <TableCell>{item.serial}</TableCell>
                                        <TableCell>
                                            {excluded ? (
                                                <Tooltip title={<h1 style={{
                                                    color: 'text.primary',
                                                    fontSize: 15,
                                                    fontWeight: 'medium'
                                                }}>{excluded.startsWith('not-found:') ? 'not-found' : excluded}</h1>}>
                                                    <Box
                                                        fontSize="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: 'red'
                                                        }}
                                                        onClick={() => handleClickOpen(item)}>
                                                        {excluded.split(':')[0]}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                <StatusTooltip item={item} onClick={handleClickOpen} />
                                            )}
                                        </TableCell>
                                        {selectedSection === 'serials' && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        if (item.serial) {
                                                            handleRemoveSerial(
                                                                item.serial
                                                            )
                                                        }
                                                    }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No saved serial items for {parseBinName(currentBin)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedItem && (
                <ExcludedDetailsDialog
                    open={open}
                    selectedItem={selectedItem}
                    onClose={handleClose}
                    onCopy={handleCopy}
                />
            )}
        </Box>
    )
}

export default SerialsEdit
